import type { TopAgentRow, SparklinePoint } from '@imza/shared';
import { Skeleton } from '../ui/Skeleton.js';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Tooltip as RTooltip,
} from 'recharts';
import { formatDistanceToNowStrict } from 'date-fns';

interface Props {
  agents: TopAgentRow[];
  isLoading: boolean;
}

function Sparkline({ data }: { data: SparklinePoint[] }) {
  if (data.length === 0) {
    return <span className="text-xs text-secondary">—</span>;
  }
  return (
    <div className="w-16 h-8" aria-hidden="true">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} barSize={4} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
          <Bar dataKey="value" fill="#0071e3" radius={[2, 2, 0, 0]} />
          <RTooltip
            contentStyle={{ fontSize: 10, padding: '2px 6px' }}
            formatter={(v) => [v, 'events']}
            labelFormatter={(l) => String(l)}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Delta({ cur, prv, label }: { cur: number; prv: number; label: string }) {
  if (cur === 0 && prv === 0) return <span className="tabular-nums">{cur}</span>;
  return (
    <span title={`${label}: ${prv} prior`} className="tabular-nums">
      {cur}
    </span>
  );
}

export function AgentLeaderboardTable({ agents, isLoading }: Props) {
  if (isLoading) {
    return (
      <div className="bg-white border border-border rounded-xl p-4 space-y-2">
        <Skeleton className="h-4 w-36 mb-3" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 rounded" />
        ))}
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="bg-white border border-border rounded-xl p-4">
        <p className="text-sm font-semibold text-primary-text mb-2">Agent leaderboard</p>
        <p className="text-sm text-secondary text-center py-4">No agent activity this period</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-border rounded-xl overflow-hidden">
      <div className="px-4 pt-4 pb-2">
        <p className="text-sm font-semibold text-primary-text">Agent leaderboard</p>
      </div>

      {/* Mobile: stacked cards */}
      <div className="sm:hidden divide-y divide-border">
        {agents.map((agent, i) => (
          <div key={agent.agentId} className="px-4 py-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-bold text-secondary w-5 flex-shrink-0">#{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-primary-text truncate">{agent.agentName}</p>
                  {agent.lastActive && (
                    <p className="text-xs text-secondary">
                      {formatDistanceToNowStrict(new Date(agent.lastActive), { addSuffix: true })}
                    </p>
                  )}
                </div>
              </div>
              <Sparkline data={agent.sparkline} />
            </div>
            <div className="grid grid-cols-3 gap-x-3 mt-2 text-xs">
              <div>
                <span className="text-secondary">Calls</span>
                <p className="font-semibold tabular-nums">{agent.calls}</p>
              </div>
              <div>
                <span className="text-secondary">WhatsApp</span>
                <p className="font-semibold tabular-nums">{agent.whatsapps}</p>
              </div>
              <div>
                <span className="text-secondary">Reached</span>
                <p className="font-semibold tabular-nums">{agent.reached}</p>
              </div>
              <div className="mt-1">
                <span className="text-secondary">Moves</span>
                <p className="font-semibold tabular-nums">{agent.stageMoves}</p>
              </div>
              <div className="mt-1">
                <span className="text-secondary">Wins</span>
                <p className="font-semibold tabular-nums text-green-600">{agent.wins}</p>
              </div>
              <div className="mt-1">
                <span className="text-secondary">Areas</span>
                <p className="font-semibold tabular-nums">{agent.areasCovered}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm" role="table" aria-label="Agent leaderboard">
          <thead>
            <tr className="border-y border-border bg-surface text-xs text-secondary">
              <th className="px-4 py-2 text-left font-semibold w-6">#</th>
              <th className="px-4 py-2 text-left font-semibold">Agent</th>
              <th className="px-3 py-2 text-right font-semibold">Calls</th>
              <th className="px-3 py-2 text-right font-semibold">WA</th>
              <th className="px-3 py-2 text-right font-semibold">Reached</th>
              <th className="px-3 py-2 text-right font-semibold">Moves</th>
              <th className="px-3 py-2 text-right font-semibold text-green-700">Wins</th>
              <th className="px-3 py-2 text-right font-semibold">Areas</th>
              <th className="px-4 py-2 text-left font-semibold">7-day</th>
              <th className="px-4 py-2 text-left font-semibold">Last active</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {agents.map((agent, i) => (
              <tr key={agent.agentId} className="hover:bg-hover-bg transition-colors">
                <td className="px-4 py-2.5 text-xs font-bold text-secondary">{i + 1}</td>
                <td className="px-4 py-2.5 font-medium text-primary-text">{agent.agentName}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{agent.calls}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{agent.whatsapps}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{agent.reached}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{agent.stageMoves}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-green-600">
                  <Delta cur={agent.wins} prv={0} label="wins" />
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{agent.areasCovered}</td>
                <td className="px-4 py-2.5">
                  <Sparkline data={agent.sparkline} />
                </td>
                <td className="px-4 py-2.5 text-xs text-secondary whitespace-nowrap">
                  {agent.lastActive
                    ? formatDistanceToNowStrict(new Date(agent.lastActive), { addSuffix: true })
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
